// What the Import files button sends, through the mutations the web already uses to create
// each thing:
//   - Recording        → CreateRecording (as the old Recordings import did), once per recorded
//                        track — several tracks are separate recordings, never joined — with
//                        `segmentStartIndices` at each GPS gap inside a track
//   - Planned hike     → CreateHike (as the old My Hikes import did), one planned trail per
//                        day, the file's waypoints as `hikeHighlightsAttributes`. A hike "from
//                        waypoints" is one day drawn straight through them, in file order.
//   - Waypoints        → CreateWaypoints then CreateWaypointGroup (as the old Waypoints import did)
//   - Recordings and a planned hike → the recordings as above, then one hike of every other line
//   - Bagged peaks     → importBaggedPeaksCsv with commit: true (as My Peaks' own CSV import did)
// The payload builders are pure; `saveImports` takes its GraphQL client and elevation lookup
// as arguments so its sequencing is unit-tested with fakes (importSave.test.ts).

import { buildImportRecordingInput } from '../../apps/import_recording/importRecordingInput';
import {
  buildCreateHikeInput,
  calculateDistances,
  dayTitle,
  processTrackData,
  simplifyPoints,
  stripEmoji,
  type ImportDay as HikeDay
} from '../helpers/gpxHikeImport';
import { titleFromFileName } from '../helpers/titleFromFileName';
import { haversineM, numberRange } from './importRules';
import type { ImportLine, ImportPoint, ImportPreview, ImportWaypoint } from './importTypes';
import { bothPlannedLines, fromWaypointsDay, plannedDays, recordingTracks, waypointsToAdd, type ImportDay } from './importPayloads';
import type { MapMatchResult } from '../models/mapMatch';
import { matchWithoutDetours } from '../models/mapMatchDetours';
import {
  addSnapSummaries,
  emptySnapSummary,
  IMPORT_MATCH_PROFILE,
  snappedDay,
  snapsToPaths,
  usableSnap,
  type DayMatch,
  type FileSnap,
  type SnapSummary
} from './importSnap';
import {
  choiceDestination,
  type ImportCounts,
  type ImportDestination,
  type ImportFileEntry,
  type ImportFileSettings,
  type PeakCsvCheck
} from './importScreenState';

/** The title a file's import is saved under: its name without the extension. */
export function importTitle(fileName: string, fallback: string): string {
  return stripEmoji(titleFromFileName(fileName)) || fallback;
}

// ---- recordings ---------------------------------------------------------------------------

/**
 * A recording's title: the file's own title when it holds one recording; otherwise the track's
 * name from the file, or the file's title numbered in time order when the track has none.
 */
export function recordingTitle(fileTitle: string, track: ImportLine, index: number, total: number): string {
  if (total <= 1) return fileTitle;
  return stripEmoji(track.name?.trim() ?? '') || `${fileTitle} (${index + 1})`;
}

/**
 * The CreateRecording input for one recorded track, keeping the file's own elevation (0 where
 * it had none) and times. Distance runs on within a segment and doesn't count the GPS gap
 * between two; each later segment's first point is a `segmentStartIndices` entry, the way a
 * continued recording is stored.
 */
export function recordingInput(track: ImportLine, title: string, clientId: string, nowIso: string) {
  const segments = track.segments.filter(seg => seg.length);
  const coords: string[] = [];
  const altitudes: number[] = [];
  const timeStamps: number[] = [];
  const segmentStartIndices: number[] = [];
  let distance = 0;
  segments.forEach((seg, si) => {
    if (si > 0) segmentStartIndices.push(coords.length);
    seg.forEach((pt, i) => {
      if (i > 0) distance += haversineM(seg[i - 1], pt);
      const ele = pt.ele ?? 0;
      coords.push(`${pt.lon} ${pt.lat} ${ele} ${Math.round(distance * 10) / 10}`);
      altitudes.push(ele);
      if (pt.time != null) timeStamps.push(pt.time);
    });
  });
  const allTimed = timeStamps.length === coords.length;
  const range = numberRange(timeStamps);
  const first = range ? range.min : null;
  const last = range ? range.max : null;
  const input = buildImportRecordingInput(
    {
      title,
      lineString: `LINESTRING (${coords.join(', ')})`,
      // Per-point times are positional on the server, so they go only when every point has one.
      timeStamps: allTimed ? timeStamps : [],
      altitudes,
      elapsedSeconds: first != null && last != null ? Math.floor((last - first) / 1000) : 0,
      createdAt: first != null ? new Date(first).toISOString() : undefined
    },
    clientId,
    nowIso
  );
  return segmentStartIndices.length ? { ...input, segmentStartIndices } : input;
}

// ---- planned hikes ------------------------------------------------------------------------

/** A file's elevation is used when at least 90% of its points carry one (spec "Elevation"). */
export function hasFileElevation(points: ImportPoint[]): boolean {
  if (!points.length) return false;
  return points.filter(p => p.ele != null).length / points.length >= 0.9;
}

/**
 * The days a planned-hike choice saves: a hike from waypoints is one day straight through the
 * kept waypoints in file order; "Recordings and a planned hike" every line that isn't a
 * recording; every other one as drawn.
 */
export function daysFor(entry: ImportFileEntry): ImportDay[] {
  const p = entry.preview as ImportPreview;
  if (entry.settings.choice === 'fromWaypoints') return [fromWaypointsDay(p)];
  if (entry.settings.choice === 'both') return plannedDays(p, entry.settings, bothPlannedLines(p));
  return plannedDays(p, entry.settings);
}

/** One day worked up for CreateHike: simplified, distances along it, titled. */
export function hikeDay(day: ImportDay, hikeTitle: string, index: number, total: number): HikeDay {
  const points = simplifyPoints(day.points.map(p => ({ lat: p.lat, lon: p.lon, elevation: p.ele ?? 0 })));
  return processTrackData(points, dayTitle(hikeTitle, { name: day.name ?? undefined, points }, index, total));
}

/** The highlights a planned hike gets from a file's waypoints, as the planner's own import makes them. */
export function highlightAttributes(waypoints: ImportWaypoint[], unnamed: (n: number) => string) {
  return waypoints.map((w, i) => ({
    type: 'other',
    title: stripEmoji(w.name.trim()) || unnamed(i + 1),
    lonlat: `POINT(${w.point.lon} ${w.point.lat})`
  }));
}

export function hikeInput(
  { hikeTitle, fileName, days, waypoints, unnamed }:
  { hikeTitle: string; fileName: string; days: HikeDay[]; waypoints: ImportWaypoint[]; unnamed: (n: number) => string }
) {
  const input = buildCreateHikeInput({ hikeTitle, fileName, days });
  return waypoints.length ? { ...input, hikeHighlightsAttributes: highlightAttributes(waypoints, unnamed) } : input;
}

// ---- waypoint groups ----------------------------------------------------------------------

/** The CreateWaypoints input, shaped as the Waypoints dialog's own import sends it. */
export function waypointsInput(waypoints: ImportWaypoint[], unnamed: string) {
  return waypoints.map(w => ({
    type: 'other',
    title: w.name.trim() || unnamed,
    description: '',
    public: true,
    coordinateReferenceSystem: 'EPSG:4326',
    lonlat: `POINT (${w.point.lon} ${w.point.lat})`
  }));
}

// ---- saving -------------------------------------------------------------------------------

/** What one saved file created, for the result toast and the host page. */
export interface ImportCreated {
  destination: ImportDestination;
  id: string;
  title: string;
  url?: string | null;
  /** How many it stands for: the peaks one CSV bagged. One when absent. */
  count?: number;
  /** What "Snap to paths" did to this file's days; absent when it wasn't ticked. */
  snap?: SnapSummary;
}

export interface ImportFailure {
  /** The entry's id, so the failed file can be sent in (importFailureReport.ts). */
  id: string;
  name: string;
  message: string;
}

export interface ImportSaveDeps {
  /** graph_client.request */
  request: (document: unknown, variables: Record<string, unknown>) => Promise<any>;
  /** [lat, lng] in, elevations out — getElevationData; null when the service is unavailable. */
  elevation: (coords: number[][]) => Promise<Array<{ lat: number; lng: number; elevation: number }> | null>;
  /** GraphHopper map matching ([lng, lat] in) — mapMatchCoordinates; only called for a snapped file. */
  mapMatch?: (coords: number[][], profile: string) => Promise<MapMatchResult>;
  mutations: { createRecording: unknown; createHike: unknown; createWaypoints: unknown; createWaypointGroup: unknown; importBaggedPeaksCsv: unknown };
  /** A bagged-peaks CSV's text, read again from the file the user added. */
  peakCsvText: (entry: ImportFileEntry) => Promise<string>;
  /** Told what each bagged-peaks CSV did, for its `peak_csv_import` event. */
  onPeaksImported?: (result: PeakCsvCheck) => void;
  /** Localised fallbacks. */
  texts: { defaultTitle: string; unnamedWaypoint: string; unnamedHighlight: (n: number) => string };
  now?: () => Date;
}

const errorsOf = (payload: any): string | null => {
  const errors = payload?.errors;
  if (!Array.isArray(errors) || !errors.length) return null;
  return errors.map((e: any) => (e.errors ? `${e.fieldName}: ${e.errors.join(', ')}` : String(e.message ?? e))).join('\n');
};

/** Replaces a day's elevations with the elevation service's, when the file had too few. */
async function withElevation(day: HikeDay, raw: ImportPoint[], deps: ImportSaveDeps): Promise<HikeDay> {
  if (hasFileElevation(raw)) return day;
  const data = await deps.elevation(day.coordinates.map(c => [c[1], c[0]]));
  if (!data || data.length !== day.coordinates.length) return day;
  const coordinates = data.map(e => [e.lng, e.lat, e.elevation]);
  return { ...day, coordinates, altitudes: data.map(e => e.elevation), distances: calculateDistances(coordinates) };
}

/** A file that saved some of its recordings before others failed: what did save is kept. */
export class ImportPartialSaveError extends Error {
  constructor(message: string, readonly created: ImportCreated[]) {
    super(message);
  }
}

async function saveRecording(track: ImportLine, title: string, deps: ImportSaveDeps): Promise<ImportCreated> {
  const now = (deps.now ?? (() => new Date()))();
  const input = recordingInput(track, title, `upload-${now.getTime()}-${Math.floor(Math.random() * 1e6)}`, now.toISOString());
  const res = await deps.request(deps.mutations.createRecording, { input });
  const created = res?.createRecording;
  const error = errorsOf(created);
  if (error || !created?.id) throw new Error(error ?? 'createRecording returned no id');
  return { destination: 'recordings', id: String(created.id), title: created.title ?? title, url: created.shareableUrl ?? null };
}

/** One recording per recorded track, each saved on its own so one failure doesn't stop the rest. */
async function saveRecordings(p: ImportPreview, fileTitle: string, deps: ImportSaveDeps): Promise<ImportCreated[]> {
  const tracks = recordingTracks(p);
  const created: ImportCreated[] = [];
  const errors: string[] = [];
  for (let i = 0; i < tracks.length; i++) {
    try {
      created.push(await saveRecording(tracks[i], recordingTitle(fileTitle, tracks[i], i, tracks.length), deps));
    } catch (e) {
      errors.push(e instanceof Error ? e.message : String(e));
    }
  }
  if (!errors.length) return created;
  if (!created.length) throw new Error(errors.join('\n'));
  throw new ImportPartialSaveError(errors.join('\n'), created);
}

/**
 * One day matched onto the paths, counted into `summary`; null when it can't be, so the caller
 * saves it as drawn. Matched on the file's own points, not the simplified ones, so the matcher
 * sees the line as the author drew it.
 */
async function snapDay(day: HikeDay, raw: ImportPoint[], deps: ImportSaveDeps, summary: SnapSummary): Promise<HikeDay | null> {
  if (raw.length < 2 || day.coordinates.length < 2) {
    summary.skipped += 1;
    return null;
  }
  try {
    const match = await matchWithoutDetours(raw.map(p => [p.lon, p.lat]), deps.mapMatch!, IMPORT_MATCH_PROFILE);
    const snapped = snappedDay(day, match);
    if (!snapped) throw new Error('Map matching returned no line');
    summary.matched += 1;
    return snapped;
  } catch (e) {
    summary.failed += 1;
    summary.errors.push(e instanceof Error ? e.message : String(e));
    return null;
  }
}

/** A day from the match made when the box was ticked, counted into `summary`; null to save it as drawn. */
function storedDay(day: HikeDay, stored: DayMatch | undefined, summary: SnapSummary): HikeDay | null {
  if (stored?.ok) {
    const snapped = snappedDay(day, stored.match);
    if (snapped) {
      summary.matched += 1;
      return snapped;
    }
    summary.failed += 1;
    summary.errors.push('Map matching returned no line');
    return null;
  }
  if (stored?.skipped) summary.skipped += 1;
  else {
    summary.failed += 1;
    summary.errors.push(stored?.error ?? 'No match for this day');
  }
  return null;
}

/**
 * One planned hike of the file's days, with its waypoints as highlights. A file with "Snap to
 * paths" ticked (a planned hike from its own lines only) saves each day matched onto the paths:
 * `stored` is the file's matches from when the box was ticked, which the save uses rather than
 * asking GraphHopper again; without them (nothing matched yet) the days are matched here.
 */
async function saveHike(entry: ImportFileEntry, title: string, deps: ImportSaveDeps, stored?: FileSnap): Promise<ImportCreated> {
  const days = daysFor(entry);
  const matches = usableSnap(entry, stored);
  const snap = snapsToPaths(entry) && (matches || deps.mapMatch) ? emptySnapSummary() : null;
  const worked: HikeDay[] = [];
  for (let i = 0; i < days.length; i++) {
    const day = hikeDay(days[i], title, i, days.length);
    const snapped = !snap
      ? null
      : matches
        ? storedDay(day, matches.days[i], snap)
        : await snapDay(day, days[i].points, deps, snap);
    // A day that didn't snap is saved exactly as it would have been without the setting.
    worked.push(snapped ?? await withElevation(day, days[i].points, deps));
  }
  const input = hikeInput({
    hikeTitle: title,
    fileName: entry.name,
    days: worked,
    waypoints: waypointsToAdd(entry.preview as ImportPreview, entry.settings),
    unnamed: deps.texts.unnamedHighlight
  });
  const res = await deps.request(deps.mutations.createHike, { input });
  const error = errorsOf(res?.createHike);
  const hike = res?.createHike?.hike;
  if (error || !hike?.id) throw new Error(error ?? 'createHike returned no hike');
  return { destination: 'plannedHikes', id: String(hike.id), title: hike.name ?? title, ...(snap ? { snap } : {}) };
}

/** The recordings, then the hike; each half saves on its own and what did save is kept. */
async function saveBoth(entry: ImportFileEntry, title: string, deps: ImportSaveDeps): Promise<ImportCreated[]> {
  const created: ImportCreated[] = [];
  const errors: string[] = [];
  try {
    created.push(...(await saveRecordings(entry.preview as ImportPreview, title, deps)));
  } catch (e) {
    if (e instanceof ImportPartialSaveError) created.push(...e.created);
    errors.push(e instanceof Error ? e.message : String(e));
  }
  try {
    created.push(await saveHike(entry, title, deps));
  } catch (e) {
    errors.push(e instanceof Error ? e.message : String(e));
  }
  if (!errors.length) return created;
  if (!created.length) throw new Error(errors.join('\n'));
  throw new ImportPartialSaveError(errors.join('\n'), created);
}

/**
 * Everything one file creates: one recording per recorded track, one hike or waypoint group,
 * or the recordings and a hike.
 */
export async function saveEntry(entry: ImportFileEntry, deps: ImportSaveDeps, stored?: FileSnap): Promise<ImportCreated[]> {
  const p = entry.preview as ImportPreview;
  const settings: ImportFileSettings = entry.settings;
  const title = importTitle(entry.name, deps.texts.defaultTitle);
  switch (settings.choice) {
    case 'recording':
      return saveRecordings(p, title, deps);
    case 'both':
      return saveBoth(entry, title, deps);
    case 'planned':
    case 'fromWaypoints':
      return [await saveHike(entry, title, deps, stored)];
    case 'waypoints': {
      const res = await deps.request(deps.mutations.createWaypoints, {
        input: { input: waypointsInput(waypointsToAdd(p, settings), deps.texts.unnamedWaypoint) }
      });
      const ids = (res?.createWaypoints ?? []).map((w: any) => w?.id).filter(Boolean);
      if (!ids.length) throw new Error('createWaypoints returned no waypoints');
      const group = await deps.request(deps.mutations.createWaypointGroup, { waypointGroup: { title, waypointIds: ids } });
      const error = errorsOf(group?.createWaypointGroup);
      if (error || !group?.createWaypointGroup?.id) throw new Error(error ?? 'createWaypointGroup returned no group');
      return [{ destination: 'waypoints', id: String(group.createWaypointGroup.id), title }];
    }
    case 'peaks': {
      const res = await deps.request(deps.mutations.importBaggedPeaksCsv, { input: { csv: await deps.peakCsvText(entry), commit: true } });
      const result: PeakCsvCheck | undefined = res?.importBaggedPeaksCsv;
      if (!result?.committed) throw new Error(result?.error ?? 'importBaggedPeaksCsv did not commit');
      deps.onPeaksImported?.(result);
      return [{ destination: 'peaks', id: entry.id, title, count: result.newCount }];
    }
    default:
      throw new Error(`${settings.choice} is not saved here`);
  }
}

/** What a save created and what failed — the result summary's content. */
export interface ImportSavedResult {
  created: ImportCreated[];
  failures: ImportFailure[];
  /** Summed over every snapped file; null when none was. */
  snap: SnapSummary | null;
  counts: ImportCounts;
  notImported: number;
}

/**
 * One file at a time, each on its own so one bad file doesn't stop the rest. `savedFiles` counts
 * the files that created anything — a file of several recordings creates several things.
 */
export async function saveImports(
  entries: ImportFileEntry[],
  deps: ImportSaveDeps,
  snaps: Record<string, FileSnap> = {}
): Promise<{ created: ImportCreated[]; failures: ImportFailure[]; savedFiles: number; snap: SnapSummary | null }> {
  const created: ImportCreated[] = [];
  const failures: ImportFailure[] = [];
  let savedFiles = 0;
  for (const entry of entries) {
    if (!choiceDestination(entry.settings.choice)) continue;
    try {
      created.push(...(await saveEntry(entry, deps, snaps[entry.id])));
      savedFiles++;
    } catch (e) {
      if (e instanceof ImportPartialSaveError) {
        created.push(...e.created);
        savedFiles++;
      }
      const message = e instanceof Error ? e.message : String(e);
      failures.push({ id: entry.id, name: entry.name, message });
    }
  }
  const summaries = created.map(c => c.snap).filter((x): x is SnapSummary => !!x);
  return { created, failures, savedFiles, snap: summaries.length ? summaries.reduce(addSnapSummaries) : null };
}
